import { TFunction } from 'i18next';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { ContractModel } from 'api/contracts/enums/ContractModel';
import { PeriodicityContracts } from 'api/contracts/enums/Periodicity';
import { Data, Field, Fields, Info, NumericKind } from 'common/components/spreadsheet/types';
import { isNumericText } from 'common/components/spreadsheet/cellValues';
import Utils from 'common/services/Utils';

// Task #66347: colunas da grelha de atualização em massa de contratos. As regras replicam as dos
// formulários do separador de contratos do ativo (useContractScreen / insuranceContractsScreen /
// maintenanceContractsScreen) — se lá mudarem, têm de mudar aqui.

export const DATE_CELL_FORMAT = 'DD/MM/YYYY';
// Aceita 1/1/2026 e 01/01/2026; vazio passa e é o `required` que decide se falta.
const DATE_REGEX = '^$|^\\d{1,2}/\\d{1,2}/\\d{4}$';

// Sub-tipo de contrato de aquisição que se comporta como uma compra: sem periodicidade, sem
// fornecedor, sem data de fim (ver UseContractScreen).
export const PURCHASE_TYPE_NAME = 'PURCHASE';
export const RENT_TYPE_NAME = 'ALUGUER';
export const FLEX_RENT_TYPE_NAME = 'FLEX_RENT';

export type ContractFieldOptions = {
    vehicles: SelectValueLabel[];
    suppliers: SelectValueLabel[];
    contractTypes: SelectValueLabel[];
    // Só para aquisição: mapa id → nome de sistema (PURCHASE, ALUGUER, FLEX_RENT, ...).
    useContractTypeNamesById?: Record<string, string>;
};

const vehicleField = (t: TFunction, vehicles: SelectValueLabel[]): Field<string> => ({
    key: 'vehicleId',
    label: t('bulk_contracts.columns.vehicle') as string,
    fieldType: { type: 'select', options: vehicles.map(v => ({ label: v.label, value: v.value })), alternateMatches: [] },
    validations: [{ rule: 'required', errorMessage: t('bulk_contracts.errors.vehicle_required') as string }],
});

const dateField = (t: TFunction, key: string, label: string, required: boolean): Field<string> => ({
    key,
    label,
    example: DATE_CELL_FORMAT,
    // Sai no ícone de informação do cabeçalho: as folhas dos clientes trazem as datas com pontos ou
    // traços e ninguém adivinha qual é o formato que a grelha quer.
    description: t('bulk_contracts.columns.date_format_description', { format: DATE_CELL_FORMAT }) as string,
    // Mesmo date picker do resto da aplicação; o valor fica na célula como texto, para a colagem do
    // Excel continuar a funcionar e o regex abaixo poder assinalar o que vier torto.
    fieldType: { type: 'date', valueFormat: DATE_CELL_FORMAT },
    validations: [
        ...(required ? [{ rule: 'required' as const, errorMessage: t('bulk_contracts.errors.field_required') as string }] : []),
        { rule: 'regex' as const, value: DATE_REGEX, errorMessage: t('bulk_contracts.errors.invalid_date', { format: DATE_CELL_FORMAT }) as string },
    ],
});

// Colunas de números: alinhadas à direita e limpas do que vem colado do Excel (símbolo da moeda,
// separadores de milhares). `money` leva sempre duas casas; `integer` (kms, anos) só as leva se o
// valor as tiver mesmo.
const numberField = (t: TFunction, key: string, label: string, numeric: NumericKind, required = false): Field<string> => ({
    key,
    label,
    fieldType: { type: 'input', numeric },
    validations: required ? [{ rule: 'required', errorMessage: t('bulk_contracts.errors.field_required') as string }] : [],
});

const periodicityOptions = (t: TFunction) => Object.keys(PeriodicityContracts).map(key => ({
    label: t('common.periodicities.' + key as any) as string,
    value: key,
}));

// A ordem das colunas segue a do template Excel da importação (ImportMaintenanceContractViewModel /
// ImportInsuranceContractViewModel): matrícula, contrato de aquisição, tipo, número, periodicidade,
// custo, início, fim, fornecedor. Só assim é que colar um bloco vindo desse ficheiro cai nas colunas
// certas — se mudarem lá, têm de mudar aqui.
const insuranceOrMaintenanceFields = (t: TFunction, model: ContractModel, options: ContractFieldOptions): Field<string>[] => [
    vehicleField(t, options.vehicles),
    {
        key: 'includeUseContractNumber',
        label: t('bulk_contracts.columns.include_use_contract_number') as string,
        description: t('bulk_contracts.columns.include_use_contract_number_description') as string,
        fieldType: { type: 'input' },
    },
    {
        key: 'contractTypeIds',
        label: t('bulk_contracts.columns.contract_types') as string,
        fieldType: { type: 'multiselect', options: options.contractTypes.map(x => ({ label: x.label, value: x.value })) },
    },
    {
        key: 'number',
        label: t('bulk_contracts.columns.number') as string,
        fieldType: { type: 'input' },
        validations: [{ rule: 'required', errorMessage: t('bulk_contracts.errors.field_required') as string }],
    },
    {
        key: 'periodicity',
        label: t('common.periodicity') as string,
        fieldType: { type: 'select', options: periodicityOptions(t), alternateMatches: [] },
        validations: [{ rule: 'required', errorMessage: t('bulk_contracts.errors.field_required') as string }],
    },
    numberField(t, 'cost', t('bulk_contracts.columns.cost') as string, 'money', true),
    dateField(t, 'startDate', t('bulk_contracts.columns.start_date') as string, true),
    dateField(t, 'endDate', t('bulk_contracts.columns.end_date') as string, true),
    {
        key: 'supplierId',
        label: t('bulk_contracts.columns.supplier') as string,
        fieldType: { type: 'select', options: options.suppliers.map(x => ({ label: x.label, value: x.value })), alternateMatches: [] },
        // Só a manutenção exige fornecedor (igual ao formulário do ativo).
        validations: model === ContractModel.MAINTENANCE
            ? [{ rule: 'required', errorMessage: t('bulk_contracts.errors.field_required') as string }]
            : [],
    },
    {
        key: 'contractFileId',
        label: (model === ContractModel.INSURANCE
            ? t('contracts.insurance_contract.insurance_certificate')
            : t('bulk_contracts.columns.contract_file')) as string,
        description: t('bulk_contracts.columns.file_description') as string,
        fieldType: { type: 'file' },
    },
];

// Ordem do template Excel (ImportUseContractViewModel): matrícula, número, tipo de financiamento,
// fornecedor, periodicidade, custo, início, fim, km contratados, custo por km extra/a menos,
// garantia (anos/kms), IUC, manutenção e seguro incluídos. As colunas que o template não tem (valor
// de entrada e nota de crédito/débito) vão para o fim, senão desalinhavam a colagem.
const useContractFields = (t: TFunction, options: ContractFieldOptions): Field<string>[] => [
    vehicleField(t, options.vehicles),
    { key: 'number', label: t('bulk_contracts.columns.number') as string, fieldType: { type: 'input' } },
    {
        key: 'useContractTypeId',
        label: t('bulk_contracts.columns.financing_type') as string,
        fieldType: { type: 'select', options: options.contractTypes.map(x => ({ label: x.label, value: x.value })), alternateMatches: [] },
        validations: [{ rule: 'required', errorMessage: t('bulk_contracts.errors.field_required') as string }],
    },
    {
        key: 'supplierId',
        label: t('bulk_contracts.columns.supplier') as string,
        fieldType: { type: 'select', options: options.suppliers.map(x => ({ label: x.label, value: x.value })), alternateMatches: [] },
    },
    {
        key: 'periodicity',
        label: t('common.periodicity') as string,
        fieldType: { type: 'select', options: periodicityOptions(t), alternateMatches: [] },
    },
    numberField(t, 'cost', t('bulk_contracts.columns.cost') as string, 'money', true),
    dateField(t, 'startDate', t('bulk_contracts.columns.start_date') as string, true),
    dateField(t, 'endDate', t('bulk_contracts.columns.end_date') as string, false),
    numberField(t, 'kmContractual', t('bulk_contracts.columns.km_contractual') as string, 'integer'),
    numberField(t, 'costPerKmOver', t('bulk_contracts.columns.cost_per_km_over') as string, 'money'),
    numberField(t, 'costPerKmUnder', t('bulk_contracts.columns.cost_per_km_under') as string, 'money'),
    numberField(t, 'warrantyYears', t('bulk_contracts.columns.warranty_years') as string, 'integer'),
    numberField(t, 'warrantyKilometers', t('bulk_contracts.columns.warranty_kilometers') as string, 'integer'),
    { key: 'stampDutyIncluded', label: t('bulk_contracts.columns.stamp_duty_included') as string, fieldType: { type: 'checkbox' } },
    { key: 'maintenanceContractIncluded', label: t('bulk_contracts.columns.maintenance_included') as string, fieldType: { type: 'checkbox' } },
    { key: 'insuranceIncluded', label: t('bulk_contracts.columns.insurance_included') as string, fieldType: { type: 'checkbox' } },
    // Fora do template Excel — daí ficarem no fim.
    numberField(t, 'entryValue', t('bulk_contracts.columns.entry_value') as string, 'money'),
    numberField(t, 'creditDebitNote', t('bulk_contracts.columns.credit_debit_note') as string, 'money'),
    numberField(t, 'kmVehicle', t('bulk_contracts.columns.km_vehicle') as string, 'integer'),
    dateField(t, 'returnDate', t('bulk_contracts.columns.return_date') as string, false),
    { key: 'returnPlace', label: t('bulk_contracts.columns.return_place') as string, fieldType: { type: 'input' } },
    {
        key: 'contractFileId',
        label: t('bulk_contracts.columns.contract_file') as string,
        description: t('bulk_contracts.columns.file_description') as string,
        fieldType: { type: 'file' },
    },
    // Anexos dos contratos que nascem dos checks — só editáveis quando o respetivo check está ligado
    // (ver lockedUseContractFields).
    {
        key: 'maintenanceFileId',
        label: t('bulk_contracts.columns.maintenance_file') as string,
        description: t('bulk_contracts.columns.maintenance_file_description') as string,
        fieldType: { type: 'file' },
    },
    {
        key: 'insuranceFileId',
        label: t('contracts.insurance_contract.insurance_certificate') as string,
        description: t('bulk_contracts.columns.insurance_file_description') as string,
        fieldType: { type: 'file' },
    },
];

export const buildContractFields = (t: TFunction, model: ContractModel, options: ContractFieldOptions): Fields<string> =>
    (model === ContractModel.USE
        ? useContractFields(t, options)
        : insuranceOrMaintenanceFields(t, model, options)) as Fields<string>;

// Nome de sistema do sub-tipo de aquisição escolhido na linha (PURCHASE / ALUGUER / FLEX_RENT / ...).
export const useContractTypeNameOf = (row: Data<string>, options: ContractFieldOptions) =>
    options.useContractTypeNamesById?.[(row.useContractTypeId as string) || ''] ?? '';

// Colunas que não se aplicam ao sub-tipo escolhido — bloqueadas e ignoradas na gravação, tal como o
// formulário do ativo esconde os campos que não fazem sentido para uma compra.
// A `row` é obrigatória: parte das regras depende do que está preenchido na linha (os anexos dos
// contratos incluídos dependem dos respetivos checks). Chamar sem ela dava-os sempre por bloqueados,
// e o rowHook limpava-os logo a seguir ao upload.
export const lockedUseContractFields = (typeName: string, row: Data<string>): string[] => {
    // Os anexos do contrato de manutenção / seguro só fazem sentido quando o respetivo check está
    // ligado — é desse check que nasce o contrato a que o ficheiro vai ficar preso.
    const includedFiles = [
        ...(row.maintenanceContractIncluded === true ? [] : ['maintenanceFileId']),
        ...(row.insuranceIncluded === true ? [] : ['insuranceFileId']),
    ];

    if (typeName === PURCHASE_TYPE_NAME) {
        return ['periodicity', 'endDate', 'entryValue', 'creditDebitNote', 'kmContractual',
            'costPerKmOver', 'costPerKmUnder', 'stampDutyIncluded', 'maintenanceContractIncluded', 'insuranceIncluded',
            // Devolução só existe em contratos que se devolvem — numa compra não.
            'returnDate', 'returnPlace', 'maintenanceFileId', 'insuranceFileId'];
    }
    if (typeName === RENT_TYPE_NAME) return ['periodicity', 'creditDebitNote', ...includedFiles];
    if (typeName === FLEX_RENT_TYPE_NAME) return ['periodicity', ...includedFiles];
    // A nota de crédito/débito só existe no flex rent.
    return ['creditDebitNote', ...includedFiles];
};

const isBlank = (value: unknown) => value === undefined || value === null || value === '';

// Task #66347: validações por linha que não cabem nas regras declarativas dos campos.
export const buildContractRowHook = (t: TFunction, model: ContractModel, options: ContractFieldOptions) =>
    (row: Data<string>, addError: (fieldKey: string, error: Info) => void): Data<string> => {
        const numericFields = ['cost', 'entryValue', 'creditDebitNote', 'kmContractual', 'warrantyYears',
            'warrantyKilometers', 'costPerKmOver', 'costPerKmUnder', 'kmVehicle'];

        numericFields.forEach(key => {
            const value = row[key];
            if (isBlank(value)) return;
            if (!isNumericText(value)) {
                addError(key, { level: 'error', message: t('bulk_contracts.errors.invalid_number') as string });
            }
        });

        const start = isBlank(row.startDate) ? null : Utils.parseToDate(row.startDate, DATE_CELL_FORMAT);
        const end = isBlank(row.endDate) ? null : Utils.parseToDate(row.endDate, DATE_CELL_FORMAT);
        if (start && end && end < start) {
            addError('endDate', { level: 'error', message: t('bulk_contracts.errors.end_before_start') as string });
        }

        if (model === ContractModel.USE) {
            const typeName = useContractTypeNameOf(row, options);
            const locked = lockedUseContractFields(typeName, row);
            // Limpa o que ficou preenchido e deixou de se aplicar (ex: mudar Leasing → Compra).
            locked.forEach(key => { if (!isBlank(row[key])) row[key] = ''; });

            if (typeName && typeName !== PURCHASE_TYPE_NAME) {
                if (isBlank(row.number)) addError('number', { level: 'error', message: t('bulk_contracts.errors.field_required') as string });
                if (isBlank(row.supplierId)) addError('supplierId', { level: 'error', message: t('bulk_contracts.errors.field_required') as string });
                if (isBlank(row.endDate)) addError('endDate', { level: 'error', message: t('bulk_contracts.errors.field_required') as string });
                if (typeName !== RENT_TYPE_NAME && typeName !== FLEX_RENT_TYPE_NAME && isBlank(row.periodicity)) {
                    addError('periodicity', { level: 'error', message: t('bulk_contracts.errors.field_required') as string });
                }
            }

            // Garantia: anos OU quilómetros (igual ao formulário do ativo).
            if (isBlank(row.warrantyYears) && isBlank(row.warrantyKilometers)) {
                const message = t('bulk_contracts.errors.warranty_required') as string;
                addError('warrantyYears', { level: 'error', message });
                addError('warrantyKilometers', { level: 'error', message });
            }
        } else if (isBlank(row.contractTypeIds)) {
            addError('contractTypeIds', { level: 'error', message: t('bulk_contracts.errors.field_required') as string });
        }

        return row;
    };
